import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import './Memories.css';

const API_ORIGIN = 'https://cabanaapi.octaviantweaking.com';
//const API_ORIGIN = 'http://localhost:5000';

const CATEGORIES = ['all', 'winter', 'summer', 'hiking', 'interior', 'wildlife', 'other'];

/* ── Firefly generator (same as gallery) ── */
function generateParticles(count) {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    size: 2 + Math.random() * 3,
    duration: 4 + Math.random() * 8,
    delay: Math.random() * 6,
    dx: (Math.random() - 0.5) * 60,
    dy: -(20 + Math.random() * 60),
    type: ['gold', 'green', 'red'][Math.floor(Math.random() * 3)],
  }));
}

const heroParticles = generateParticles(12);
const fireflyParticles = generateParticles(20);

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatBytes(bytes) {
  if (bytes == null) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/* ═══════════════════════════════════════════════════════════════
   MEMORIES — Immersive Gallery Redesign
   ═══════════════════════════════════════════════════════════════ */
export default function Memories() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();

  /* ── State ── */
  const [items, setItems] = useState([]);
  const [listBusy, setListBusy] = useState(false);
  const [likedIds, setLikedIds] = useState(new Set());

  const [files, setFiles] = useState([]);
  const [caption, setCaption] = useState('');
  const [uploadCategory, setUploadCategory] = useState('other');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [toastMsg, setToastMsg] = useState(null);

  const canUpload = useMemo(() => !!user && !authLoading, [user, authLoading]);
  const galleryRef = useRef(null);

  /* ── Toast ── */
  const showToast = useCallback((msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3500);
  }, []);

  /* ── Data ── */
  const refresh = useCallback(async () => {
    setListBusy(true);
    setError('');
    try {
      const data = await api.listMemories();
      setItems(data.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setListBusy(false);
    }
  }, []);

  const fetchLikes = useCallback(async () => {
    if (!user) return;
    try {
      const data = await api.getMyLikes();
      setLikedIds(new Set(data.likes || []));
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { fetchLikes(); }, [fetchLikes]);

  /* ── Filtered ── */
  const filteredItems = useMemo(() => {
    if (activeFilter === 'all') return items;
    return items.filter(it => it.category === activeFilter);
  }, [items, activeFilter]);

  /* ── Stats ── */
  const stats = useMemo(() => ({
    photos: items.length,
    contributors: new Set(items.map(i => i.user_name)).size,
    likes: items.reduce((sum, i) => sum + (i.likes_count || 0), 0),
  }), [items]);

  /* ── Scroll lock ── */
  useEffect(() => {
    document.body.style.overflow = isUploadOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isUploadOpen]);

  useEffect(() => {
    document.body.style.overflow = lightboxIndex !== null ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [lightboxIndex]);

  /* ── Like toggle ── */
  const toggleLike = async (e, id) => {
    e.stopPropagation();
    if (!user) { showToast(t('memories.guestNotice')); return; }
    try {
      const data = await api.toggleMemoryLike(id);
      setLikedIds(prev => {
        const next = new Set(prev);
        if (data.liked) next.add(id); else next.delete(id);
        return next;
      });
      setItems(prev => prev.map(it =>
        it.id === id
          ? { ...it, likes_count: (it.likes_count || 0) + (data.liked ? 1 : -1) }
          : it
      ));
      if (data.liked) showToast('♥ ' + (t('memories.liked') || 'Liked!'));
    } catch (err) {
      showToast(err.message);
    }
  };

  /* ── File handling ── */
  const onChooseFiles = (e) => setFiles(Array.from(e.target.files || []));

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (dropped.length) setFiles(dropped);
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  /* ── Upload ── */
  const onUpload = async (e) => {
    e.preventDefault();
    setError('');
    if (!canUpload) { setError(t('memories.guestNotice')); return; }
    if (!files.length) { setError('Choose at least one photo.'); return; }

    setBusy(true);
    try {
      await api.uploadMemories(files, caption, uploadCategory);
      setFiles([]);
      setCaption('');
      setUploadCategory('other');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setIsUploadOpen(false);
      await refresh();
      showToast(t('memories.publishButton') + ' 🎉');
    } catch (e2) {
      setError(e2.message);
    } finally {
      setBusy(false);
    }
  };

  /* ── Lightbox ── */
  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const goPrev = (e) => { e.stopPropagation(); setLightboxIndex(prev => (prev > 0 ? prev - 1 : filteredItems.length - 1)); };
  const goNext = (e) => { e.stopPropagation(); setLightboxIndex(prev => (prev < filteredItems.length - 1 ? prev + 1 : 0)); };

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') setLightboxIndex(p => (p > 0 ? p - 1 : filteredItems.length - 1));
      if (e.key === 'ArrowRight') setLightboxIndex(p => (p < filteredItems.length - 1 ? p + 1 : 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, filteredItems.length]);

  const getSrc = (it) => {
    if (!it?.url) return '';
    return it.url.startsWith('http') ? it.url : `${API_ORIGIN}${it.url.startsWith('/') ? it.url : `/${it.url}`}`;
  };

  /* ── Masonry sizing pattern ── */
  const getSize = (i) => {
    const patterns = [
      { r: 1, c: 1 }, { r: 2, c: 1 }, { r: 1, c: 2 },
      { r: 1, c: 1 }, { r: 1, c: 1 }, { r: 2, c: 2 },
      { r: 1, c: 1 }, { r: 1, c: 2 }, { r: 2, c: 1 },
      { r: 1, c: 1 }, { r: 1, c: 1 }, { r: 1, c: 1 },
    ];
    return patterns[i % patterns.length];
  };

  /* ── Scroll to gallery ── */
  const scrollToGallery = () => {
    galleryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /* ═══ RENDER ═══ */
  return (
    <div className="mem">
      {/* ── TOAST ── */}
      <div className={`mem-toast ${toastMsg ? 'mem-toast--show' : ''}`}>
        <span>✨</span>
        <span>{toastMsg}</span>
      </div>

      {/* ── UPLOAD MODAL ── */}
      {isUploadOpen && createPortal(
        <div className="mem-upload-overlay" onClick={() => setIsUploadOpen(false)}>
          <div className="mem-upload-modal" onClick={(e) => e.stopPropagation()}>
            <button className="mem-upload-modal__close" onClick={() => setIsUploadOpen(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="mem-upload-modal__header">
              <div className="mem-upload-modal__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
              </div>
              <h2 className="mem-upload-modal__title">{t('memories.addJournal') || 'Share a Memory'}</h2>
              <p className="mem-upload-modal__sub">{t('memories.uploadPrompt')}</p>
            </div>

            {!user && !authLoading && (
              <div className="mem-upload-modal__notice">
                {t('memories.guestNotice')} <strong>{t('memories.signInUpload')}</strong> {t('memories.toUpload')}
              </div>
            )}

            <form onSubmit={onUpload} className="mem-upload-form">
              <div className="mem-upload-field">
                <label className="mem-upload-label">{t('memories.caption')}</label>
                <input
                  className="mem-upload-input"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder={t('memories.captionPlaceholder')}
                  maxLength={200}
                />
              </div>

              <div className="mem-upload-field">
                <label className="mem-upload-label">Category</label>
                <div className="mem-cat-pills">
                  {CATEGORIES.filter(c => c !== 'all').map(cat => (
                    <button
                      key={cat}
                      type="button"
                      className={`mem-cat-pill ${uploadCategory === cat ? 'mem-cat-pill--active' : ''}`}
                      onClick={() => setUploadCategory(cat)}
                    >
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mem-upload-field">
                <label className="mem-upload-label">{t('memories.photos')}</label>
                <div
                  className={`mem-drop-zone ${dragOver ? 'mem-drop-zone--hover' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                >
                  <input
                    ref={fileInputRef}
                    className="mem-drop-zone__input"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={onChooseFiles}
                    disabled={!canUpload || busy}
                  />
                  <div className="mem-drop-zone__content">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="42" height="42">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <span className="mem-drop-zone__text">{t('memories.dropPhotos')}</span>
                    <span className="mem-drop-zone__hint">{t('memories.supportFiles')} • {t('memories.maxImages')}</span>
                  </div>
                </div>
              </div>

              {files.length > 0 && (
                <div className="mem-file-queue">
                  {files.slice(0, 10).map((f, idx) => (
                    <div key={f.name + f.size} className="mem-file-item">
                      <div className="mem-file-thumb">
                        <img src={URL.createObjectURL(f)} alt="" />
                      </div>
                      <div className="mem-file-info">
                        <span className="mem-file-name">{f.name}</span>
                        <span className="mem-file-size">{formatBytes(f.size)}</span>
                      </div>
                      <button type="button" className="mem-file-remove" onClick={() => removeFile(idx)}>✕</button>
                    </div>
                  ))}
                  {files.length > 10 && (
                    <div style={{ fontSize: '0.78rem', color: 'var(--m-dim)', textAlign: 'center', fontStyle: 'italic' }}>
                      Only the first 10 files will be uploaded.
                    </div>
                  )}
                </div>
              )}

              {error && <div className="mem-upload-error">{error}</div>}

              <button
                type="submit"
                disabled={!canUpload || busy || files.length === 0}
                className="mem-upload-submit"
              >
                {busy
                  ? <><span className="mem-upload-spinner" /> {t('memories.publishing')}</>
                  : <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg> {t('memories.publishButton')}</>
                }
              </button>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* ═══ HERO ═══ */}
      <header className="mem-hero">

        {/* Full-bleed background photo with Ken Burns effect */}
        <div className="mem-hero__bg" aria-hidden="true">
          <img
            className="mem-hero__bg-img"
            src="/gallery/medium/cabana piatra buru-61.jpg"
            alt=""
            fetchpriority="high"
          />
          <div className="mem-hero__veil" />
          <div className="mem-hero__grain" />
        </div>

        {/* Floating particles */}
        <div className="mem-hero__particles" aria-hidden="true">
          {heroParticles.map((p) => (
            <span
              key={p.id}
              className={`mem-hero__particle mem-hero__particle--${p.type}`}
              style={{
                left: p.left,
                top: p.top,
                width: p.size,
                height: p.size,
                animationDuration: `${p.duration}s`,
                animationDelay: `${p.delay}s`,
                '--dx': `${p.dx}px`,
                '--dy': `${p.dy}px`,
              }}
            />
          ))}
        </div>

        {/* Central content — unique centered cinematic layout */}
        <div className="mem-hero__stage">

          {/* Thin top rule with eyebrow text */}
          <div className="mem-hero__eyebrow" aria-hidden="true">
            <span className="mem-hero__eyebrow-rule" />
            <span className="mem-hero__eyebrow-text">
              {t('memories.label') || 'Cabana Piatra Buru'}
            </span>
            <span className="mem-hero__eyebrow-rule" />
          </div>

          {/* Main title — large italic serif, no accent split */}
          <h1 className="mem-hero__title">
            <span className="mem-hero__title-small">{t('memories.heroTitleStart') || 'Our'}</span>
            <span className="mem-hero__title-big">{t('memories.heroTitleAccent') || 'Memories'}</span>
          </h1>

          {/* Decorative corner-frame around title area */}
          <div className="mem-hero__frame" aria-hidden="true">
            <span className="mem-hero__frame-corner mem-hero__frame-corner--tl" />
            <span className="mem-hero__frame-corner mem-hero__frame-corner--tr" />
            <span className="mem-hero__frame-corner mem-hero__frame-corner--bl" />
            <span className="mem-hero__frame-corner mem-hero__frame-corner--br" />
          </div>

          {/* Subtitle */}
          <p className="mem-hero__subtitle">{t('memories.subtitle')}</p>

          {/* Live stats — inline horizontal row */}
          <div className="mem-hero__stats">
            <div className="mem-hero__stat">
              <span className="mem-hero__stat-value">{stats.photos}</span>
              <span className="mem-hero__stat-label">{t('memories.stats.photos') || 'Memories'}</span>
            </div>
            <span className="mem-hero__stat-divider" aria-hidden="true">✦</span>
            <div className="mem-hero__stat">
              <span className="mem-hero__stat-value">{stats.contributors}</span>
              <span className="mem-hero__stat-label">{t('memories.stats.contributors') || 'Guests'}</span>
            </div>
            <span className="mem-hero__stat-divider" aria-hidden="true">✦</span>
            <div className="mem-hero__stat">
              <span className="mem-hero__stat-value">{stats.likes}</span>
              <span className="mem-hero__stat-label">{t('memories.stats.likes') || 'Likes'}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="mem-hero__actions">
            <button className="mem-hero__cta" onClick={() => setIsUploadOpen(true)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              {t('memories.addJournal') || 'Share a Memory'}
            </button>
            <button className="mem-hero__scroll-btn" onClick={scrollToGallery}>
              {t('memories.latest') || 'Browse Gallery'}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="mem-hero__scroll-hint" onClick={scrollToGallery}>
          <span className="mem-hero__scroll-line" />
          <span>{t('gallery.hero.scroll') || 'Scroll'}</span>
        </div>

      </header>

      {/* ═══ CONTENT ═══ */}
      <div className="mem-content" ref={galleryRef}>

        {/* Gallery header */}
        <div className="mem-gallery-head">
          <h2 className="mem-gallery-head__title">{t('memories.latest')}</h2>
          <button className="mem-gallery-head__refresh" onClick={refresh} disabled={listBusy} title="Refresh">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18" className={listBusy ? 'spinning' : ''}>
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
          </button>
        </div>

        {/* Filters */}
        <div className="mem-filters">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              className={`mem-filter-pill ${activeFilter === cat ? 'mem-filter-pill--active' : ''}`}
              onClick={() => setActiveFilter(cat)}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>

        {/* Gallery grid */}
        <div className="mem-grid" aria-busy={listBusy ? 'true' : 'false'}>
          {listBusy && items.length === 0 && (
            [...Array(8)].map((_, idx) => (
              <div key={idx} className="mem-skeleton" />
            ))
          )}

          {!listBusy && filteredItems.map((it, i) => {
            const src = getSrc(it);
            const size = getSize(i);
            const isLiked = likedIds.has(it.id);

            return (
              <figure
                key={it.id}
                className={`mem-card mem-card--r${size.r} mem-card--c${size.c}`}
                onClick={() => openLightbox(i)}
              >
                <img className="mem-card__img" src={src} alt={it.caption || 'Guest memory'} loading="lazy" />

                <div className="mem-card__top">
                  <span className={`mem-card__badge mem-card__badge--${it.category || 'other'}`}>
                    {it.category || 'other'}
                  </span>
                </div>

                <figcaption className="mem-card__info">
                  {it.caption && <div className="mem-card__caption">{it.caption}</div>}
                  <div className="mem-card__meta">
                    {it.user_name && <span className="mem-card__author">{t('memories.by')} {it.user_name}</span>}
                    <span className="mem-card__date">{formatDate(it.created_at)}</span>
                  </div>
                </figcaption>

                <div className="mem-card__actions">
                  <button
                    className={`mem-action-btn ${isLiked ? 'mem-action-btn--liked' : ''}`}
                    onClick={(e) => toggleLike(e, it.id)}
                    title={t('memories.like')}
                  >
                    {isLiked ? '♥' : '♡'} <span>{it.likes_count || 0}</span>
                  </button>
                  <button
                    className="mem-action-btn"
                    onClick={(e) => { e.stopPropagation(); openLightbox(i); }}
                    title="Expand"
                  >
                    ⤢
                  </button>
                </div>
              </figure>
            );
          })}

          {!listBusy && filteredItems.length === 0 && (
            <div className="mem-empty">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="52" height="52">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <h3>{t('memories.noUploads')}</h3>
              <button className="mem-share-btn" onClick={() => setIsUploadOpen(true)}>
                {t('memories.addJournal')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ═══ LIGHTBOX ═══ */}
      {lightboxIndex !== null && filteredItems[lightboxIndex] && (
        <div className="mem-lightbox" onClick={closeLightbox}>
          <button className="mem-lightbox__close" onClick={closeLightbox}>
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <button className="mem-lightbox__nav mem-lightbox__nav--prev" onClick={goPrev}>
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <div className="mem-lightbox__body" onClick={(e) => e.stopPropagation()}>
            <img
              className="mem-lightbox__img"
              src={getSrc(filteredItems[lightboxIndex])}
              alt={filteredItems[lightboxIndex].caption || 'Memory'}
            />
            <div className="mem-lightbox__info">
              <div className="mem-lightbox__meta">
                <div className="mem-lightbox__caption">{filteredItems[lightboxIndex].caption || ''}</div>
                <div className="mem-lightbox__author">
                  {filteredItems[lightboxIndex].user_name
                    ? `${t('memories.by')} ${filteredItems[lightboxIndex].user_name}`
                    : ''}
                  {filteredItems[lightboxIndex].created_at && (
                    <span className="mem-lightbox__date"> · {formatDate(filteredItems[lightboxIndex].created_at)}</span>
                  )}
                </div>
              </div>
              <div className="mem-lightbox__actions">
                <button
                  className={`mem-action-btn mem-lightbox__like ${likedIds.has(filteredItems[lightboxIndex].id) ? 'mem-action-btn--liked' : ''}`}
                  onClick={(e) => toggleLike(e, filteredItems[lightboxIndex].id)}
                >
                  {likedIds.has(filteredItems[lightboxIndex].id) ? '♥' : '♡'}
                  <span>{filteredItems[lightboxIndex].likes_count || 0}</span>
                </button>
                <span className={`mem-card__badge mem-card__badge--${filteredItems[lightboxIndex].category || 'other'}`}>
                  {filteredItems[lightboxIndex].category || 'other'}
                </span>
              </div>
            </div>
            <div className="mem-lightbox__counter">
              {lightboxIndex + 1} / {filteredItems.length}
            </div>
          </div>

          <button className="mem-lightbox__nav mem-lightbox__nav--next" onClick={goNext}>
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
